import { Badge, Tooltip } from '@heroui/react';
import type { Guild, GuildFolder } from '@/types/api';
import { IdentityAvatar } from './IdentityAvatar';
import { useLongPress } from '@/lib/useLongPress';
import { FolderIcon } from '@/components/icons';
import { railFolderLabel, railUnreadLabel } from './guildRail/folderRules';
import { voiceMarkerLabel } from './guildRail/railVoice';
import { TooltipTarget } from '@/components/ui/TooltipTarget';

/**
 * Collapsed server-folder tile for the rail. Shows a 2×2 preview of the first
 * four member icons over the folder's accent tint, an aggregated unread badge,
 * and acts as a drop target (drag a server onto it to add it to the folder).
 * Click toggles expansion. Better-than-Discord touch: the preview minis are
 * the real server icons, and the whole tile glows in the folder colour.
 */
export function FolderTile(props: {
  folder: GuildFolder;
  /** Vertical (cispracs) rail — show the tooltip to the side, not on top. */
  vertical?: boolean;
  members: Guild[];
  expanded: boolean;
  unread: number;
  /** People in voice across every server in the folder. */
  voiceCount?: number;
  active: boolean;
  isDropTarget: boolean;
  onToggle: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  /** Touch long-press opener — opens the same menu as `onContextMenu` at the
   *  press coordinates. Optional so callers that don't need touch can omit it. */
  onContextMenuAt?: (x: number, y: number) => void;
  dropProps?: React.HTMLAttributes<HTMLButtonElement> & { 'data-folder-drop'?: string };
}) {
  const { folder, members } = props;
  const tint = folder.color ?? 'rgb(var(--bk-accent))';
  const preview = members.slice(0, 4);
  const label = railFolderLabel(folder, members.map((g) => g.name));
  const voiceLabel = voiceMarkerLabel(props.voiceCount ?? 0);
  // Touch long-press → same folder context menu as desktop right-click.
  const longPress = useLongPress((x, y) => props.onContextMenuAt?.(x, y));

  return (
    <Tooltip delay={0}>
      <TooltipTarget>
        <button
          aria-label={label}
          type="button"
          onClick={props.onToggle}
          onContextMenu={props.onContextMenu}
          aria-expanded={props.expanded}
          {...props.dropProps}
          {...longPress}
          className={[
            // `rail-badges` keeps the corner chips inside this box — the tile
            // clips (overflow-hidden rounds the previews), so a badge pushed
            // past the edge loses its outer half outright.
            'rail-badges relative flex size-12 shrink-0 select-none items-center justify-center overflow-hidden rounded-[6px] outline-none transition-[transform,box-shadow] [-webkit-touch-callout:none] hover:scale-105 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
            props.isDropTarget ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface scale-105' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          // A restrained tint distinguishes folders without outlining every tile.
          style={{
            backgroundColor: `color-mix(in srgb, ${tint} ${props.active ? 16 : 7}%, var(--surface))`,
          }}
        >
          <Badge.Anchor>
            {props.expanded || preview.length === 0 ? (
              // Expanded (the servers are already visible below) → a plain folder
              // glyph. This used to be a hand-drawn vault door, described in the
              // code as echoing the brand mark: the mark is now the «B», and a
              // vault door already means «Сейф» elsewhere in the app, so the
              // shape was saying the wrong word in two places at once.
              <span className="grid size-9 place-items-center" style={{ color: tint }}>
                <FolderIcon className="size-6" />
              </span>
            ) : (
              <div className="grid size-9 grid-cols-2 grid-rows-2 gap-0.5">
                {preview.map((g) => <IdentityAvatar key={g.id} id={g.id} name={g.name} src={g.iconUrl} size="xs" className="!size-full !rounded-[5px] !text-[9px]" />)}
              </div>
            )}
            {/* A collapsed folder speaks for its servers: somebody inside is in
                voice. Same green chip as a server tile carries. */}
            {voiceLabel !== null && (
              <Badge color="success" size="sm" placement="bottom-right">
                <span className="tabular-nums">{voiceLabel}</span>
              </Badge>
            )}
            {/* Accent, not danger: unread is something to catch up on, not an
                error, and the same colour marks it on the servers themselves. */}
            {railUnreadLabel(props.unread) && (
              <Badge color="accent" size="sm">
                {railUnreadLabel(props.unread)}
              </Badge>
            )}
          </Badge.Anchor>
        </button>
      </TooltipTarget>
      <Tooltip.Content showArrow placement={props.vertical ? 'right' : 'bottom'}>
        <Tooltip.Arrow />
        {label}
      </Tooltip.Content>
    </Tooltip>
  );
}
